import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ajustarProntoDoEscritorio, fetchEstilosDoEscritorio } from '@/api/editor';
import {
  AmostraDoFormato,
  CamposDoFormato,
  diferencaDeFormato,
  formatoDoPronto,
  resumoDoFormato,
  type FormatoParagrafo,
} from '@/components/editor-forense';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { classifyError, notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';

/** O padrão do app: o Corpo da peça sem nenhum ajuste. */
const BASE = (): FormatoParagrafo => formatoDoPronto('corpo', false);

/**
 * O "Corpo da peça" de um escritório (a aba Peças dele): os mesmos campos do
 * "Gerenciar estilos" do editor (`CamposDoFormato`), gravados como o ajuste do pronto
 * do escritório — pelo id dele, não pelo principal de quem edita.
 */
export function EditorDoCorpo({ escritorioId }: { escritorioId: string }) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const chave = queryKeys.editor.estilosDoEscritorio(escritorioId);
  // Relê ao abrir: o "Gerenciar estilos" do editor muda o mesmo Corpo por outra chave.
  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: chave,
    queryFn: () => fetchEstilosDoEscritorio(escritorioId),
    refetchOnMount: 'always',
  });
  const ajuste = data?.ajustesProntos?.corpo;
  const [f, setF] = useState<FormatoParagrafo | null>(null);
  if (data && !f && !isFetching) setF({ ...BASE(), ...(ajuste ?? {}) });

  const gravar = useMutation({
    mutationFn: (ajustes: Partial<FormatoParagrafo> | null) => ajustarProntoDoEscritorio(escritorioId, 'corpo', ajustes),
    onSuccess: async (salvo) => {
      queryClient.setQueryData(chave, salvo);
      setF({ ...BASE(), ...(salvo.ajustesProntos?.corpo ?? {}) });
      // O editor de quem está no escritório relê os estilos (a mesma raiz de chave).
      await queryClient.invalidateQueries({ queryKey: queryKeys.editor.estilos });
      showSuccess('Corpo da peça salvo: os parágrafos nele mudam juntos.');
    },
    onError: (err) => notifyMutationError(err, showError),
  });

  if (isLoading || (!isError && !f)) {
    return (
      <div className="flex justify-center py-4">
        <Spinner size={32} label="Carregando o Corpo da peça" />
      </div>
    );
  }
  if (isError || !f) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{classifyError(error).message}</AlertDescription>
      </Alert>
    );
  }
  const pode = !!data?.podeEditarEscritorio;
  const ajustado = !!ajuste && Object.keys(ajuste).length > 0;
  const d = diferencaDeFormato(BASE(), f);

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">{resumoDoFormato(f)}{ajustado ? '' : ' (o padrão do app)'}</p>
      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,16rem)]">
        <CamposDoFormato f={f} aoMudar={(p) => setF({ ...f, ...p })} desabilitado={!pode} />
        <AmostraDoFormato f={f} />
      </div>
      {pode && (
        <div className="flex flex-wrap justify-end gap-2">
          {ajustado && (
            <Button variant="outline" onClick={() => gravar.mutate(null)} disabled={gravar.isPending}>
              Restaurar o padrão
            </Button>
          )}
          <Button onClick={() => gravar.mutate(Object.keys(d).length ? d : null)} disabled={gravar.isPending}>
            {gravar.isPending && <Spinner size={16} className="mr-2 text-inherit" />}
            Salvar Corpo da peça
          </Button>
        </div>
      )}
    </div>
  );
}
